import {
  concatStyleSets,
  FontWeights,
  HighContrastSelector,
  IButtonStyles,
  IRawStyle,
  ITheme,
} from "@fluentui/react";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import {
  ActionControlSemanticColors,
  AppColors,
  baseFabricFocusAfterSelector,
  baseFabricFocusVisibleSelector,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineHoverFocus,
  getActionControlSemanticColorsWithTheme,
  getInnerBorderStyles,
  getModernUxExperimentPreference,
} from "@1js/acui-common";
import { isThemingEnabled } from "@1js/acui-theme";

import {
  modernUxRibbonStylingConstants,
  getRibbonTopBarStylingConstants,
} from "../../ribbonStylingConstants";
import { IPivotHeaderStyles, IPivotStyles } from "../Pivot";
import { ConnectedTabState } from "./ConnectedTabState";

const contextualTabClassName = "contextualTab";

// style used to make an inner border on a contextual tab
// switch to the browser managed highlight color for high contrast
const highlightTextBorderColorStyle = {
  selectors: {
    [HighContrastSelector]: {
      borderColor: "HighlightText",
    },
  },
};

const getPivotHeaderStyles = memoizeFunction(
  (
    actionControlSemanticColors: ActionControlSemanticColors,
    pivotHeaderHeight?: number
  ): IButtonStyles => {
    const height = getModernUxExperimentPreference()
      ? modernUxRibbonStylingConstants.tabHeaderHeight
      : pivotHeaderHeight || getRibbonTopBarStylingConstants().tabHeaderHeight;
    const fontWeight = getModernUxExperimentPreference()
      ? FontWeights.regular
      : FontWeights.semibold;
    const borderRadius = getModernUxExperimentPreference()
      ? modernUxRibbonStylingConstants.tabHeaderBorderRadius
      : 0;

    return {
      root: {
        backgroundColor: actionControlSemanticColors.tabHeaderBackgroundColor,
        color: actionControlSemanticColors.textColor,
        cursor: "default",
        height,
        borderRadius,
        selectors: {
          [`${baseFabricFocusVisibleSelector} &:focus:after`]: {
            outline: "none",
            border: "none",
          },
        },
      },
      rootHovered: {
        borderColor: "transparent",
        color: actionControlSemanticColors.textColor,
        fontWeight,
        backgroundColor:
          actionControlSemanticColors.tabHeaderHoverBackgroundColor,
      },
      rootPressed: {
        backgroundColor:
          actionControlSemanticColors.tabHeaderPressedBackgroundColor,
        fontWeight,
      },
      rootFocused: [
        {
          fontWeight,
        },
      ],
      rootDisabled: {
        backgroundColor: actionControlSemanticColors.defaultBackgroundColor,
        color: actionControlSemanticColors.textDisabledColor,
      },
    };
  }
);

// styles to be applied on top of pivot header styles on the selected tab header
// note: concat of style sets happens in pivotheader component
const getSelectedPivotHeaderStyleOverrides = memoizeFunction(
  (actionControlSemanticColors: ActionControlSemanticColors) => {
    return {
      root: {
        fontWeight: FontWeights.semibold,
        backgroundColor:
          actionControlSemanticColors.activeTabHeaderBackgroundColor,
        color: actionControlSemanticColors.activeTabHeaderTextColor,
        ["&:focus:after"]: getInnerBorderStyles({
          width: 2,
          color: actionControlSemanticColors.focusedBorderColor,
          gap: -1,
        }),
      },
      rootHovered: {
        fontWeight: FontWeights.semibold,
        backgroundColor:
          actionControlSemanticColors.activeTabHeaderHoverBackgroundColor,
        color: actionControlSemanticColors.activeTabHeaderHoverTextColor,
      },
      rootPressed: {
        fontWeight: FontWeights.semibold,
        backgroundColor:
          actionControlSemanticColors.activeTabHeaderPressedBackgroundColor,
        color: actionControlSemanticColors.activeTabHeaderPressedTextColor,
      },
      rootFocused: {
        fontWeight: FontWeights.semibold,
        borderColor: actionControlSemanticColors.focusedBorderColor,
        [baseFabricFocusAfterSelector]: getInnerBorderStyles({
          width: 2,
          color: actionControlSemanticColors.focusedBorderColor,
          gap: -1,
        }),
      },
    };
  }
);

const getContextualTabStylesOverrides = memoizeFunction(
  (
    actionControlSemanticColors: ActionControlSemanticColors,
    appColors: AppColors,
    isSelected: boolean,
    connectedTabState: ConnectedTabState = ConnectedTabState.None
  ): IButtonStyles => {
    const color = isThemingEnabled()
      ? actionControlSemanticColors.contextualTabHeaderTextColor
      : appColors.primary;
    const fontWeight = FontWeights.semibold;
    return concatStyleSets(
      {
        root: [
          contextualTabClassName,
          {
            color,
            backgroundColor:
              actionControlSemanticColors.contextualTabHeaderBackgroundColor,
            fontWeight,
            width:
              "100%" /* eliminates space between adjacent, grouped contextual tabs */,
          },
          getConnectedTabStyleOverrides(isSelected, connectedTabState),
        ],
        rootHovered: {
          color,
          fontWeight,
          backgroundColor:
            actionControlSemanticColors.contextualTabHeaderHoverBackgroundColor,
        },
        rootPressed: {
          color,
          fontWeight,
        },
      },
      isSelected &&
        getModernUxExperimentPreference() &&
        getSelectedPivotHeaderStyleOverrides(actionControlSemanticColors)
    );
  }
);

function getConnectedTabStyleOverrides(
  selected: boolean,
  connectedTabState: ConnectedTabState
): IRawStyle | undefined {
  let borderOverrides: IRawStyle | undefined;
  const rect: IRawStyle = {
    left: 2,
    top: 2,
    bottom: 2,
    right: 2,
  };

  const selectedStyleOverride = selected
    ? {
        selectors: {
          "&:after": highlightTextBorderColorStyle,
        },
      }
    : undefined;

  if (connectedTabState) {
    switch (connectedTabState) {
      case ConnectedTabState.First:
        borderOverrides = {
          borderRightWidth: 0,
        };
        rect.right = 1;
        break;
      case ConnectedTabState.Last:
        borderOverrides = {
          borderLeftWidth: 0,
        };
        rect.left = 1;
        break;
      default:
        break;
    }
  }
  return {
    selectors: {
      [HighContrastSelector]: [borderOverrides, selectedStyleOverride],
      // second selector below is needed to be as specific as the one added in base button styles (styles by fabric). Then last one (this one) wins
      [`&:after, ${baseFabricFocusVisibleSelector} &:focus:after`]: [
        {
          content: '""',
          position: "absolute",
          border: `1px solid transparent`,
        },
        rect,
      ],
      // similar to above, this complex set of selectors is necessary because tab headers use base button styles from fabric and the selectors for
      // focus styles classes were long and specific. So basically one-upping those styles by adding a custom class name
      [`.${contextualTabClassName}&:hover:after, ${baseFabricFocusVisibleSelector} .${contextualTabClassName}&:focus:after`]:
        highlightTextBorderColorStyle,
    },
  };
}

export const overflowButtonWidth = 30;

const getOverflowButtonStyles = memoizeFunction(
  (actionControlSemanticColors: ActionControlSemanticColors): IButtonStyles => {
    const textColor = isThemingEnabled()
      ? actionControlSemanticColors.textColor
      : "#444444";
    const pressedBackgroundColor = isThemingEnabled()
      ? actionControlSemanticColors.tabHeaderPressedBackgroundColor
      : "#c8c8c8";
    const styles: IButtonStyles = {
      root: {
        width: overflowButtonWidth,
        padding: 0,
        color: textColor,
        backgroundColor: actionControlSemanticColors.tabHeaderBackgroundColor,
      },
      rootPressed: {
        backgroundColor: pressedBackgroundColor,
      },
      rootExpanded: {
        backgroundColor: pressedBackgroundColor,
        color: textColor,
      },
      icon: {
        width: 12,
        height: 12,
        lineHeight: 12,
      },
      rootHovered: [
        {
          backgroundColor: isThemingEnabled()
            ? actionControlSemanticColors.tabHeaderHoverBackgroundColor
            : "#e1e1e1",
        },
        // High contrast styles taken from PivotHeader.styles to be consistent
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      rootFocused: [
        {
          backgroundColor: isThemingEnabled()
            ? actionControlSemanticColors.tabHeaderBackgroundColor
            : "#e1e1e1",
          [baseFabricFocusAfterSelector]: getInnerBorderStyles({
            width: 2,
            color: actionControlSemanticColors.tabHeaderFocusedBorderColor,
            gap: -1,
          }),
        },
        // High contrast styles taken from PivotHeader.styles to be consistent
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
    };

    return concatStyleSets(
      getPivotHeaderStyles(actionControlSemanticColors),
      styles
    );
  }
);

export const getTabHeaderStyles = memoizeFunction(
  (
    theme: ITheme,
    tabHeight: number,
    styles?: Partial<IPivotHeaderStyles>
  ): Partial<IPivotHeaderStyles> => {
    const actionControlSemanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    return {
      pivotHeader: concatStyleSets(
        getPivotHeaderStyles(actionControlSemanticColors, tabHeight),
        styles && styles.pivotHeader
      ),
      selectedPivotHeader: concatStyleSets(
        getSelectedPivotHeaderStyleOverrides(actionControlSemanticColors),
        styles && styles.selectedPivotHeader
      ),
      wrapper: styles && styles.wrapper,
      ghostPivotLabel: styles && styles.ghostPivotLabel,
      indicator: styles && styles.indicator,
    };
  }
);

export const getContextualTabHeaderStyles = memoizeFunction(
  (
    theme: ITheme,
    appColors: AppColors,
    tabHeight: number,
    connectedTabState?: ConnectedTabState
  ): Partial<IPivotHeaderStyles> => {
    const actionControlSemanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    const tabHeaderStyles = getTabHeaderStyles(theme, tabHeight);
    const contextualTabStylesOverrides = getContextualTabStylesOverrides(
      actionControlSemanticColors,
      appColors,
      false /* isSelected */,
      connectedTabState
    );
    const selectedContextualTabStylesOverrides =
      getContextualTabStylesOverrides(
        actionControlSemanticColors,
        appColors,
        true /* isSelected */,
        connectedTabState
      );
    return {
      pivotHeader: concatStyleSets(
        tabHeaderStyles.pivotHeader,
        contextualTabStylesOverrides
      ),
      selectedPivotHeader: concatStyleSets(
        tabHeaderStyles.selectedPivotHeader,
        selectedContextualTabStylesOverrides
      ),
    };
  }
);

export const getTabListStyles: (theme: ITheme) => IPivotStyles =
  memoizeFunction((theme: ITheme) => {
    const actionControlSemanticColors: ActionControlSemanticColors =
      getActionControlSemanticColorsWithTheme(theme);
    return {
      root: [
        getModernUxExperimentPreference() && {
          backgroundColor: actionControlSemanticColors.tabHeaderBackgroundColor,
          borderRadius: `0 ${modernUxRibbonStylingConstants.tabHeaderBorderRadius}px ${modernUxRibbonStylingConstants.tabHeaderBorderRadius}px 0`,
        },
      ],
      overflowButton: getOverflowButtonStyles(actionControlSemanticColors),
    };
  });
